/**
 * 사내 공통 AI 프롬프트 설정 패널 — 메일함 사이드바의 "설정 → AI 프롬프트(관리자)" 진입점.
 *
 * 권한이 있는 사용자(GLOBAL_ADMIN / MAIL_RULE_GLOBAL) 만 사이드바에서 이 항목을 보게 되므로
 * 컴포넌트 자체는 권한 체크를 다시 하지 않는다 (백엔드 PUT 도 권한 가드).
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from "@mui/material";
import SaveRoundedIcon       from "@mui/icons-material/SaveRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";

import { mailApi } from "../../api/mailApi";
import ActionButton from "../../../../component/common/ActionButton";
import { notify }  from "../../../../util/toast";

export default function AdminPromptPanel() {
    const [prompt, setPrompt]     = useState("");
    const [original, setOriginal] = useState("");
    const [loading, setLoading]   = useState(true);
    const [saving, setSaving]     = useState(false);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await mailApi.getAiPromptSetting();
            const p = res?.prompt ?? res?.data?.prompt ?? "";
            setPrompt(p); setOriginal(p);
        } catch (e) {
            notify(e?.response?.data?.message || "프롬프트 로드 실패", "error");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const dirty = prompt !== original;

    const handleSave = async () => {
        if (!prompt.trim()) {
            notify("프롬프트가 비어있습니다", "warning");
            return;
        }
        setSaving(true);
        try {
            const res = await mailApi.saveAiPromptSetting(prompt);
            const saved = res?.prompt ?? res?.data?.prompt ?? prompt;
            setPrompt(saved); setOriginal(saved);
            notify("저장되었습니다 — 모든 직원의 AI 검토에 즉시 반영됩니다.", "success");
        } catch (e) {
            notify(e?.response?.data?.message || "저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", mb: 0.3 }}>
                AI 프롬프트 (관리자)
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 2 }}>
                사내 공통 AI 메일 검토 프롬프트입니다.
            </Typography>

            <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
                여기서 저장한 프롬프트는 <b>모든 직원의 AI 메일 검토 패널</b>에서 기본값으로 사용됩니다.
                개인이 작성기에서 일시적으로 편집할 수는 있지만, 이 페이지의 값이 사내 공식 기준입니다.
            </Alert>

            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", mb: 1 }}>
                AI 검토 프롬프트
            </Typography>
            <TextField
                multiline minRows={12} maxRows={28} fullWidth
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={loading || saving}
                placeholder="AI 가 메일 본문을 검토할 때 따라야 하는 가이드라인을 작성하세요…"
                sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, fontFamily: '"Malgun Gothic", system-ui' } }}
            />

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1.2}>
                <ActionButton
                    variant="save"
                    onClick={handleSave}
                    loading={saving}
                    disabled={!dirty || loading}
                />
                <ActionButton
                    variant="secondary"
                    startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                    disabled={!dirty || saving}
                    onClick={() => setPrompt(original)}
                    label="되돌리기"
                />
            </Stack>
        </Box>
    );
}
